import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { Search, Mail, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";

const PAGE_SIZE = 50;

export default function PlatformOutbox() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState([]);
  const [emailType, setEmailType] = useState("all");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/platform/tenants").then((r) => setTenants(r.data || []));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (emailType !== "all") params.set("email_type", emailType);
      if (tenantFilter !== "all") params.set("tenant_id", tenantFilter);
      params.set("limit", String(PAGE_SIZE));
      params.set("skip", String(page * PAGE_SIZE));
      const { data } = await api.get(`/platform/outbox?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTypes(data.types || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [emailType, tenantFilter, page]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter(
      (e) =>
        (e.to || "").toLowerCase().includes(s) ||
        (e.subject || "").toLowerCase().includes(s) ||
        (e.body || "").toLowerCase().includes(s),
    );
  }, [items, q]);

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="space-y-5" data-testid="platform-outbox">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">Serpix Admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("platform.nav.outbox")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {t("platform.outbox.subtitle", { count: total })}
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="platform-outbox-search"
            placeholder={t("platform.outbox.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={tenantFilter} onValueChange={(v) => { setPage(0); setTenantFilter(v); }}>
          <SelectTrigger className="w-48" data-testid="platform-outbox-tenant-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("platform.outbox.all_tenants")}</SelectItem>
            {tenants.map((tn) => (
              <SelectItem key={tn.id} value={tn.id}>{tn.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={emailType} onValueChange={(v) => { setPage(0); setEmailType(v); }}>
          <SelectTrigger className="w-44" data-testid="platform-outbox-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("platform.outbox.all_types")}</SelectItem>
            {types.map((tt) => (
              <SelectItem key={tt} value={tt}>{tt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/70 text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">To</th>
              <th className="px-4 py-3 text-left font-semibold">Subject</th>
              <th className="px-4 py-3 text-left font-semibold">Tenant</th>
              <th className="px-4 py-3 text-left font-semibold">Type</th>
              <th className="px-4 py-3 text-right font-semibold">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {loading && (
              <tr><td colSpan="5" className="px-6 py-8 text-center text-zinc-500">{t("common.loading")}</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="5" className="px-6 py-8 text-center text-zinc-400">{t("platform.outbox.empty")}</td></tr>
            )}
            {filtered.map((e) => (
              <tr
                key={e.id}
                onClick={() => setSelected(e)}
                data-testid={`platform-outbox-row-${e.id}`}
                className="cursor-pointer hover:bg-zinc-50/60"
              >
                <td className="px-4 py-3 max-w-[220px]">
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="truncate text-zinc-900">{e.to}</span>
                  </div>
                </td>
                <td className="px-4 py-3 max-w-[320px]">
                  <div className="truncate font-medium text-zinc-800">{e.subject}</div>
                </td>
                <td className="px-4 py-3 text-zinc-600 max-w-[180px]">
                  <div className="truncate">{e.tenant_name || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-mono font-semibold text-violet-700">
                    {e.meta?.event_type || e.type || "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-zinc-500 text-[11px]">
                  {new Date(e.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-zinc-500" data-testid="platform-outbox-pagination">
        <div>
          {t("platform.users.showing", {
            from: page * PAGE_SIZE + 1,
            to: Math.min(total, (page + 1) * PAGE_SIZE),
            total,
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <span className="text-xs">{page + 1} / {maxPage + 1}</span>
          <Button size="sm" variant="ghost" onClick={() => setPage(Math.min(maxPage, page + 1))} disabled={page >= maxPage}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="platform-outbox-detail">
          <SheetHeader>
            <SheetTitle className="pr-6">{selected?.subject}</SheetTitle>
            <SheetDescription>
              <span className="font-mono text-[11px]">{selected?.to}</span>
              {selected?.tenant_name && (
                <>
                  <span className="mx-1">·</span>
                  <span>{selected.tenant_name}</span>
                </>
              )}
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                {new Date(selected.created_at).toLocaleString()}
              </div>
              <div className="whitespace-pre-wrap rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-800">
                {selected.body}
              </div>
              {selected.meta && (
                <details className="text-[11px] text-zinc-500">
                  <summary className="cursor-pointer font-semibold">meta</summary>
                  <pre className="mt-1 rounded bg-zinc-900 p-3 text-zinc-100 overflow-x-auto text-[10px]">
                    {JSON.stringify(selected.meta, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
